/**
 * Beam Music reconciler — pure HTMLAudioElement driver.
 *
 * Called from `_applyMusicStateToAudio` in LiveRoomPage.js on every
 * `beamMusicState` change (host + listener). Extracted so the direct
 * track-switch + stale-metadata-handler guarantees are unit-testable
 * without rendering the full LiveRoomPage tree.
 *
 * Guarantees:
 *   • When the authoritative `src` changes, pauses the element and
 *     calls `load()` BEFORE assigning the new src. Required on iOS
 *     Safari — reassigning src on a playing element without a prior
 *     pause leaves the element attached to the old playback session
 *     and the new source never actually starts (that is why the host
 *     previously had to Pause before switching).
 *   • Detaches any still-pending `loadedmetadata` handler from an
 *     earlier switch before starting a new one, so a slow metadata
 *     load from track A cannot restart playback after the user has
 *     already moved on to track B / C.
 *   • Stale-callback guard: deferred play/seek bails if `el.src`
 *     has moved past the src this handler was scheduled for.
 *   • Preserves the existing 1.5 s drift threshold.
 *   • Preserves the monotonic clock-sync baseline math.
 *   • Preserves the iOS autoplay-blocked overlay signalling via
 *     `setAudioBlocked`.
 *
 * Options:
 *   backendUrl       prefix for relative src_url values
 *   baseline         clock-sync baseline captured on the latest WS
 *                    `music_state_update` frame (nullable)
 *   handlerRef       { current: null } ref holding the pending
 *                    `loadedmetadata` handler so a subsequent switch
 *                    can detach it
 *   setAudioBlocked  React state setter — called with true when
 *                    autoplay is rejected, false when it succeeds
 */
export const DRIFT_THRESHOLD_S = 1.5;

const _computeTargetPosition = (ms, baseline) => {
  if (!ms) return 0;
  if (baseline && (!ms.started_at || baseline.ms?.started_at === ms.started_at)) {
    const monoNow = (typeof performance !== "undefined" ? performance.now() : Date.now());
    const monoElapsedMs = Math.max(0, monoNow - baseline.monoAtReceive);
    const startedAtServerMs = Date.parse(ms.started_at || baseline.serverTimeAtReceive) || 0;
    const serverNowEstMs = baseline.serverTimeAtReceive + monoElapsedMs;
    const elapsed = ms.is_playing && startedAtServerMs
      ? Math.max(0, (serverNowEstMs - startedAtServerMs) / 1000)
      : 0;
    return (ms.position_s_at_start || 0) + elapsed;
  }
  const startedAt = Date.parse(ms.started_at || ms.updated_at || "") || Date.now();
  const elapsed = Math.max(0, (Date.now() - startedAt) / 1000);
  return (ms.position_s_at_start || 0) + (ms.is_playing ? elapsed : 0);
};

export const applyMusicStateToAudio = (el, ms, opts) => {
  if (!el) return;
  const { backendUrl = "", baseline = null, handlerRef, setAudioBlocked } = opts || {};
  const _setBlocked = (v) => {
    try { if (setAudioBlocked) setAudioBlocked(v); } catch (_e) {}
  };
  const _detachPending = () => {
    if (handlerRef && handlerRef.current) {
      try { el.removeEventListener("loadedmetadata", handlerRef.current); } catch (_e) {}
      handlerRef.current = null;
    }
  };

  if (!ms || !ms.track_id || !ms.track_meta?.src_url) {
    try { el.pause(); } catch (_e) {}
    try { if (el.src) el.src = ""; } catch (_e) {}
    _detachPending();
    _setBlocked(false);
    return;
  }

  const rawSrc = ms.track_meta.src_url;
  const src = rawSrc.startsWith("http") ? rawSrc : `${backendUrl}${rawSrc}`;
  const target = _computeTargetPosition(ms, baseline);
  const srcChanged = el.src !== src;

  if (srcChanged) {
    // iOS Safari — pause + load before swapping src, otherwise the
    // new source never actually starts for the listener.
    try { el.pause(); } catch (_e) {}
    _detachPending();
    try { el.src = src; } catch (_e) {}
    try { el.load(); } catch (_e) {}
  }

  const applySeekAndPlay = () => {
    // Stale-callback guard: another track swapped the src since this
    // handler was scheduled → do nothing.
    if (el.src !== src) return;
    try {
      if (Math.abs((el.currentTime || 0) - target) > DRIFT_THRESHOLD_S) {
        el.currentTime = target;
      }
    } catch (_e) {}
    if (ms.is_playing) {
      const p = el.play();
      if (p && typeof p.catch === "function") {
        p.then(() => _setBlocked(false)).catch(() => _setBlocked(true));
      }
    } else {
      try { el.pause(); } catch (_e) {}
      _setBlocked(false);
    }
  };

  // Fast path: track unchanged OR metadata already loaded → run now.
  if (!srcChanged
      || (typeof el.readyState === "number" && el.readyState >= 1 /* HAVE_METADATA */)) {
    applySeekAndPlay();
    return;
  }

  // Defer until the browser signals metadata is ready for the new src.
  const handler = () => {
    if (handlerRef) handlerRef.current = null;
    applySeekAndPlay();
  };
  if (handlerRef) handlerRef.current = handler;
  try { el.addEventListener("loadedmetadata", handler, { once: true }); } catch (_e) {}
};
